import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { PIRTypeEnum } from '@/api/enum/common.enum';
import { useRef, useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import {
	DISPLAY_DATE_FORMAT,
	DISPLAY_DATE_TIME_FORMAT,
	DISPLAY_DATETIME_12H_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useDeleteFamilyMutation,
	useGetFamilyDetailQuery,
	useGetFamilyFormDetailQuery,
	useQueryFamilyGroupListQuery,
} from '@/api/family-management/familyApi';
import { showLabel } from '@/utils/helper';
import {
	LANGUAGE_LIST,
	LANGUAGE_ZH_LIST,
	ONOFF,
	YESNO,
} from '@/common/constants/common';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
	useGetExportCustomerPDFMutation,
	useGetFamilyGenerateReportMutation,
} from '@/api/common';
import FormManagementChip from '@/common/components/FormManagementChip';
import LogHistory from '@/pages/pre-admission/forms-management/LogHistory';

export default function FamilyDetailZhPage() {
	const { t } = useTranslation();
	const { familyDetailFormId } = useParams();
	const navigate = useNavigate();

	const [serviceDetailsExpanded, setServiceDetailsExpanded] = useState(true);
	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getFamilyGenerateReport] = useGetFamilyGenerateReportMutation();

	const { data: details } = useGetFamilyFormDetailQuery(
		familyDetailFormId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.familyMemberApplicationFormDetails')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc || ''}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response = await getFamilyGenerateReport(
									details?.data.id || ''
								);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
					</Box>
				</Box>

				<Card
					sx={{
						padding: ' 24px 24px 0',
						marginTop: 3,
					}}
				>
					<Accordion
						expanded={serviceDetailsExpanded}
						onChange={() => setServiceDetailsExpanded(!serviceDetailsExpanded)}
						sx={{
							boxShadow: 'none!important',
							'&::before': {
								backgroundColor: 'transparent !important',
								border: 'none!important',
							},
							'& .MuiAccordionSummary-root': {
								padding: '0!important',
							},
							'& .MuiAccordionSummary-content': {
								margin: '0!important',
							},
							'& .MuiAccordionDetails-root': {
								padding: '0!important',
							},
						}}
					>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Typography
								variant='h6'
								component='span'
								sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
							>
								{t('family.serviceDetails')}
							</Typography>
						</AccordionSummary>

						<AccordionDetails>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={6}>
									<AppFieldView
										label={t('family.memberNo')}
										value={details?.data.familyMemberFileNo}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.fileNo')}
										value={details?.data.fileNo}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('common.unit')}
										value={details?.data.branchName}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.handledBy')}
										value={details?.data.handledByName}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('member.lastModified')}
										value={
											!details?.data.updatedAt
												? '-'
												: dayjs(details?.data.updatedAt)
														.locale('en')
														.format(DISPLAY_DATE_TIME_FORMAT)
										}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.status')}
										value={details?.data.formStatusDesc}
									/>
								</Grid>
								<Grid size={12}>
									<Typography variant='body1'>
										{t('family.tobeMember')}
									</Typography>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.twc')}
										value={details?.data.twcMember ? '是' : '否'}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.familySupportServices')}
										value={details?.data.fssMember ? '是' : '否'}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.familyAlliance')}
										value={details?.data.familyNetworkMember ? '是' : '否'}
									/>
								</Grid>

								<Grid size={12}>
									<Typography variant='body1'>{t('family.accept')}</Typography>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.phone')}
										value={details?.data.acceptPhoneCalls ? '是' : '否'}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.mail')}
										value={details?.data.acceptMails ? '是' : '否'}
									/>
								</Grid>
								<Grid size={12}>
									<AppFieldView
										label={t('family.email')}
										value={details?.data.acceptEmails ? '是' : '否'}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.twc')}
										value={details?.data.acceptTwcCommunications ? '是' : '否'}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.familySupportServices')}
										value={details?.data.acceptFssCommunications ? '是' : '否'}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.familyAlliance')}
										value={
											details?.data.acceptFamilyNetworkCommunications
												? '是'
												: '否'
										}
									/>
								</Grid>
								<Grid size={12}>
									<Typography variant='body1'>
										{t('family.interesting')}
									</Typography>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.organize')}
										value={
											details?.data.interestedInOrganizingActivities
												? '是'
												: '否'
										}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.volunteer')}
										value={
											details?.data.interestedInVolunteerActivities
												? '是'
												: '否'
										}
									/>
								</Grid>
							</Grid>

							<Divider sx={{ margin: '24px 0' }} />
							<Typography
								variant='h6'
								component='span'
								sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
							>
								{t('family.rehabiliteeInfo')}
							</Typography>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={12}>
									<AppFieldView
										label={t('family.rehabiliteeMember')}
										value={details?.data.pirTypeDesc}
									/>
								</Grid>
								{(details?.data.pirType === PIRTypeEnum.MemberOfThisCentre ||
									details?.data.pirType ===
										PIRTypeEnum.NonMemberOfThisCentre) && (
									<>
										<Grid size={6}>
											<AppFieldView
												label={t('family.pirNameZh')}
												value={details?.data.pirNameZh}
											/>
										</Grid>
										<Grid size={6}>
											<AppFieldView
												label={t('preAdmission.zh_sex')}
												value={details?.data.pirSexDesc}
											/>
										</Grid>
										<Grid size={6}>
											<AppFieldView
												label={t('preAdmission.birthday')}
												value={
													details?.data.pirDateOfBirth
														? dayjs(details?.data.pirDateOfBirth)
																.locale('en')
																.format(DISPLAY_DATE_FORMAT)
														: ''
												}
											/>
										</Grid>
										<Grid size={6}>
											<AppFieldView
												label={t('family.pirUnit')}
												value={details?.data.pirUnit}
											/>
										</Grid>
										<Grid size={6}>
											<AppFieldView
												label={t('family.pirFileNo')}
												value={details?.data.pirFileNo}
											/>
										</Grid>
										<Grid size={6}>
											<AppFieldView
												label={t('family.relationshipToPir')}
												value={details?.data.relationshipToPirDesc}
											/>
										</Grid>
										<Grid size={6}>
											<AppFieldView
												label={t('family.liveWithPir')}
												value={details?.data.liveWithPir ? '是' : '否'}
											/>
										</Grid>
									</>
								)}
							</Grid>

							<Divider sx={{ margin: '24px 0' }} />
							<Typography
								variant='h6'
								component='span'
								sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
							>
								{t('family.applicantInfo')}
							</Typography>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.zh_name_en')}
										value={details?.data.nameZh}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.en_name_en')}
										value={details?.data.nameEn}
									/>
								</Grid>

								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.zh_sex')}
										value={details?.data.sexDesc}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.idNumber')}
										value={details?.data.hkid}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.birthday')}
										value={
											details?.data.dateOfBirth
												? dayjs(details?.data.dateOfBirth)
														.locale('en')
														.format(DISPLAY_DATE_FORMAT)
												: ''
										}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.getJob')}
										value={details?.data.familyMemberWorkStatusDesc}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.occupation')}
										value={details?.data.familyMemberOccupationDesc}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.zh_education')}
										value={details?.data.educationDesc}
									/>
								</Grid>

								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.phone')}
										value={details?.data.phone}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.homePhone')}
										value={details?.data.homeTel}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.zh_email')}
										value={details?.data.email}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.zh_addressLanguage')}
										value={showLabel(
											details?.data.addressLanguage,
											LANGUAGE_ZH_LIST
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.territory')}
										value={details?.data.regionDesc}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.area')}
										value={details?.data.districtDesc}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.village')}
										value={details?.data.subdistrictDesc}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.estate')}
										value={details?.data.estateCourtVillageDesc}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.street')}
										value={details?.data.roadStreet}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.streetNumber')}
										value={details?.data.roadStreetNo}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.block')}
										value={details?.data.buildingBlockDesc}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.blockDetail')}
										value={details?.data.buildingBlockSpecify}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.storey')}
										value={details?.data.floor}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('preAdmission.chamber')}
										value={details?.data.flat}
									/>
								</Grid>
							</Grid>

							<Divider sx={{ margin: '24px 0' }} />
							<Typography
								variant='h6'
								component='span'
								sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
							>
								{t('family.statement')}
							</Typography>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('family.statementTips')}
									</Typography>
								</Grid>
								<Grid size={12}>
									<AppFieldView
										label={t('family.isAgree')}
										value={
											details?.data.consentPersonalDataUsage ? '同意' : '不同意'
										}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.recovery')}
										value={details?.data.consentTWC ? '是' : '否'}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.familyAlliance')}
										value={details?.data.consentFamilyNetwork ? '是' : '否'}
									/>
								</Grid>
								<Grid size={12}>
									<AppFieldView
										label={t('family.signatureDate')}
										value={
											!details?.data.applicationSignatureDate
												? '-'
												: dayjs(details?.data.applicationSignatureDate)
														.locale('en')
														.format(DISPLAY_DATE_FORMAT)
										}
									/>
								</Grid>

								<Grid size={6}>
									<AppFieldView
										label={t('family.staffName')}
										value={details?.data.handledByName}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.staffSignatureDate')}
										value={
											!details?.data.handlingSignatureDate
												? '-'
												: dayjs(details?.data.handlingSignatureDate)
														.locale('en')
														.format(DISPLAY_DATE_FORMAT)
										}
									/>
								</Grid>
							</Grid>

							<Divider sx={{ margin: '24px 0' }} />
							<Typography
								variant='h6'
								component='span'
								sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
							>
								{t('family.serviceUnit')}
							</Typography>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={12}>
									<AppFieldView
										label={t('family.acceptApplication')}
										value={details?.data.acceptApplication ? '是' : '否'}
									/>
								</Grid>
								{details?.data.acceptApplication === false && (
									<Grid size={12}>
										<AppFieldView
											label={t('family.noAdoptReason')}
											value={details?.data.nonAcceptanceReason}
										/>
									</Grid>
								)}
								<Grid size={12}>
									<AppFieldView
										label={t('family.staffSignDate')}
										value={
											!details?.data.workerSignatureDate
												? '-'
												: dayjs(details?.data.workerSignatureDate)
														.locale('en')
														.format(DISPLAY_DATE_FORMAT)
										}
									/>
								</Grid>
							</Grid>

							<Divider sx={{ margin: '24px 0' }} />
							<Typography
								variant='h6'
								component='span'
								sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
							>
								{t('family.remark')}
							</Typography>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={12}>
									<AppFieldView
										label={t('family.rehabiliteeRemark')}
										value={details?.data.pirRemarks}
									/>
								</Grid>
								<Grid size={12}>
									<AppFieldView
										label={t('family.applyRemark')}
										value={details?.data.applicantRemarks}
									/>
								</Grid>
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
